import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  schema?: object;
}

export function useSEO({ title, description, keywords, canonical, schema }: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Canonical URL
    const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonicalUrl);

    // Last modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:type', 'website', true);

    // Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript && schema) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, schema, location.pathname, siteUrl]);
}

// Schema.org generators
export const generateOrganizationSchema = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'DN Biotech',
  url: siteUrl,
  logo: 'https://static.readdy.ai/image/f774deb3335ef344be2f4793f6ebd557/d3524caf242f33efd937ef8bb569492a.png',
  description: 'Professional research supply company specializing in high-purity peptides and biotech compounds for laboratory use.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1302 Waugh Dr. #413',
    addressLocality: 'Houston',
    addressRegion: 'TX',
    postalCode: '77019',
    addressCountry: 'US'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Service',
    email: 'support@shopdailynest.com',
    availableLanguage: 'English'
  }
});

export const generateWebPageSchema = (siteUrl: string, path: string, title: string, description: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: title,
  description: description,
  url: `${siteUrl}${path}`,
  publisher: {
    '@type': 'Organization',
    name: 'DN Biotech',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/f774deb3335ef344be2f4793f6ebd557/d3524caf242f33efd937ef8bb569492a.png'
    }
  }
});

export const generateProductSchema = (siteUrl: string, product: any) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: `${product.name} ${product.specification} - Research grade peptide with ${product.purity} purity`,
  image: product.image,
  sku: product.id,
  brand: {
    '@type': 'Brand',
    name: 'DN Biotech'
  },
  offers: {
    '@type': 'Offer',
    price: product.price,
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: `${siteUrl}/product/${product.slug}`,
    priceValidUntil: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  }
});

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(faq => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer
    }
  }))
});
